import "../../../crm/jobs/components/JobInvoiceSendDrawer.css";
import GrowthSignal from "../../../../components/growth/GrowthSignal";
import { useEffect, useRef, useState } from "react";
import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import { hasRole, useHub } from "../../../../core/Global";
import { API_ROUTES } from "../../../../core/Constants";
import { JobsReportRequest, JobsReportSort } from "../models/JobsReportRequest";
import { DataColumnsVisibility, JobsColumnsVisibility, TechPayColumnsVisibility, visibilityToQuery } from "../models/JobReportModel";
import { JobsChartSegments } from "../../../../core/Enums";
import { AddonTypes } from "../../../system/settings/models/UsersAddonModel";
import { FontAwesomeIcons } from "../../../../core/Types";
import PhoneItem from "../../../../models/PhoneItem";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import Icon from "../../../../components/base/Icon";
import Button from "../../../../components/base/Button";
import Checkbox from "../../../../components/form/Checkbox";
import Tagify from "../../../../components/tagify/Tagify";
import TextArea from "../../../../components/form/TextArea";
import PhoneInput from "../../../../components/form/PhoneInput";
import FlexRow from "../../../../components/base/FlexRow";

type SendChannel = "email" | "phone" | "wa";

interface IProps {
    body: JobsReportRequest
    sort: JobsReportSort
    techId?: string
    query?: string
    jobVis?: JobsColumnsVisibility
    dataVis?: DataColumnsVisibility
    techVis?: TechPayColumnsVisibility
    defaultSegmentId?: string
    onSent?: () => void
    onClose: () => void
}

const DRAWER_ID = "drawer-job-reports-send";

const CHANNELS: { key: SendChannel; label: string; icon: FontAwesomeIcons; iconType?: "solid" | "brands" }[] = [
    { key: "email", label: "Email", icon: "envelope" },
    { key: "phone", label: "SMS", icon: "comment-sms" },
    { key: "wa", label: "WhatsApp", icon: "whatsapp", iconType: "brands" }
];

export default function JobReportSendDrawer(props: IProps) {

    const hub = useHub();
    const drawerRef = useRef<DrawerRef>(null);

    const [sendTo, sendToSet] = useState<SendChannel>("email");
    const [pdf, pdfSet] = useState<boolean>(true);
    const [csv, csvSet] = useState<boolean>(false);
    const [recipients, recipientsSet] = useState<string[]>();
    const [message, messageSet] = useState<string>();
    const [phone, phoneSet] = useState<PhoneItem>();

    // Pre-filled contact details of the technician (when sending a single tech's report).
    const [defaultPhone, defaultPhoneSet] = useState<PhoneItem>();
    const [defaultWhatsapp, defaultWhatsappSet] = useState<PhoneItem>();

    const [sending, sendingSet] = useState<boolean>(false);
    const [downloading, downloadingSet] = useState<boolean>(false);
    const [waActive, waActiveSet] = useState<boolean | undefined>(undefined);

    const canManageAddons = hasRole("SystemSettings");

    useEffect(() => { window.hideFormError("format"); }, [pdf, csv]);

    useEffect(() => {
        if (props.techId) {
            recipientsSet([props.techId]);

            // Auto-fill the technician's phone & WhatsApp numbers (WhatsApp falls back to phone).
            hub.Get("/reports/jobs/recipient/" + props.techId, res => {
                if (res.success && res.data) {
                    if (res.data.phone) defaultPhoneSet(res.data.phone);
                    if (res.data.whatsapp) defaultWhatsappSet(res.data.whatsapp);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.techId]);

    useEffect(() => {
        hub.Get("/accounts/profile/addons", res => {
            const wa = (res?.data as any[] | undefined)?.find(a => a.addonType === AddonTypes.WhatsApp);
            waActiveSet(!!wa?.active);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openAddons = () => window.open("/system/finance/subscription", "_blank");
    const waBlocked = sendTo === "wa" && waActive === false;

    // Column visibility -> query string. The generated files come from these flags
    // regardless of the delivery channel, so the same logic the modal used is preserved.
    const buildVisibilityQuery = (): string => {
        let q = "";

        if (props.jobVis &&
            (props.body.segmentIds.length > 0 || props.defaultSegmentId
                || props.body.segment == JobsChartSegments.CanceledJobs
                || props.body.segment == JobsChartSegments.ClosedJobs)) {
            q = "?" + visibilityToQuery(props.jobVis);
        }

        if (props.dataVis &&
            (props.body.segmentIds.length == 0
                && props.body.segment != JobsChartSegments.CanceledJobs
                && props.body.segment != JobsChartSegments.ClosedJobs)) {
            q = "?" + visibilityToQuery(props.dataVis);
        }

        if (props.techVis && props.body.segment == JobsChartSegments.Payments && props.body.segmentIds.length == 0) {
            q = "?" + visibilityToQuery(props.techVis);
        }

        if (q == "") q = "?a=1";
        if (props.query) q += "&" + props.query;
        if (csv) q += "&csv=True";
        if (pdf) q += "&pdf=True";
        return q;
    };

    const validFormat = (): boolean => {
        if (!pdf && !csv) {
            window.formError("format", "Please select at least 1 export format!", DRAWER_ID);
            return false;
        }
        return true;
    };

    const send = () => {
        window.hideFormErrors();

        let valid = validFormat();

        if (sendTo === "email") {
            if (!recipients || recipients.length < 1) {
                window.formError("recipients", "Please choose at least 1 recipient", DRAWER_ID);
                valid = false;
            }
        } else {
            const digits = (phone?.phone ?? "").replace(/\D/g, "");
            if (digits.length < 9) {
                window.formError("phone", "Please write a valid phone number!", DRAWER_ID);
                valid = false;
            }
        }

        if (!valid) return;

        sendingSet(true);

        const payload: any = {
            ...props.body,
            ...props.sort,
            segmentIds: props.defaultSegmentId ? [props.defaultSegmentId] : props.body.segmentIds,
            sendTo,
            message
        };

        if (sendTo === "email") {
            payload.recipients = recipients;
            payload.exportAndSend = true;
        } else {
            payload.phone = phone?.phone;
            payload.phoneCountryCode = phone?.countryCode;
        }

        hub.Post("/reports/jobs/export/" + buildVisibilityQuery(), payload, res => {
            sendingSet(false);
            if (res.success) {
                toastSuccess(sendTo === "email"
                    ? "Report has been sent successfully!"
                    : "Report link has been sent successfully!");
                props.onSent?.();
                drawerRef.current?.close();
            }
            else toastError(res.message ?? "Could not send the report. Please try again.");
        }, () => {
            // Network/server failure — stop the spinner and let the user know instead of hanging.
            sendingSet(false);
            toastError("Could not send the report. Please try again.");
        });
    };

    const download = () => {
        window.hideFormErrors();
        if (!validFormat()) return;

        downloadingSet(true);
        hub.Post("/reports/jobs/export/" + buildVisibilityQuery(), {
            ...props.body,
            ...props.sort,
            exportAndDownload: true
        }, res => {
            downloadingSet(false);
            if (res.success) {
                if (res.data?.pdf) window.open(res.data.pdf, "_blank");
                if (res.data?.csv) window.open(res.data.csv, "_blank");
            }
            else toastError(res.message ?? "Could not prepare the file. Please try again.");
        }, () => {
            downloadingSet(false);
            toastError("Could not prepare the file. Please try again.");
        });
    };

    return (
        <Drawer ref={drawerRef} id={DRAWER_ID} title="Send Report" onClose={props.onClose}>
            <div className="pt-3">

                <div className="fw-bold fs-7 mb-2">Send via</div>
                <div className="d-flex mb-6">
                    {
                        CHANNELS.map(ch =>
                            <label key={ch.key}
                                className={"jis-channel flex-fill d-flex align-items-center justify-content-center me-3 " + (sendTo === ch.key ? "active" : "")}>
                                <input type="radio" className="d-none" checked={sendTo === ch.key}
                                    onChange={e => { if (e.target.checked) sendToSet(ch.key); }} />
                                <Icon icon={ch.icon} type={ch.iconType} size={16} className="me-2" />
                                <span className="fs-7 fw-bold">{ch.label}</span>
                            </label>
                        )
                    }
                </div>

                <FlexRow className="mb-3">
                    <div className="fs-6 fw-bold">File Format</div>
                    <div id="format-err" className="small text-danger mx-3 form-error"></div>
                </FlexRow>
                <FlexRow className="mb-6">
                    <Checkbox label="PDF" checked={pdf} onChange={e => pdfSet(e)} className="me-5" />
                    <Checkbox label="CSV" checked={csv} onChange={e => csvSet(e)} />
                </FlexRow>

                {
                    sendTo === "email" &&
                    <Tagify id="tagify-report-send-emails" block freeTags email closeOnSelect
                        label="Send to" labelUp apiUrl={API_ROUTES.TagifyEmailsAutocomplete}
                        selectedId={props.techId}
                        onChange={e => recipientsSet(e)} />
                }

                {
                    sendTo === "phone" &&
                    <PhoneInput id="txt-report-send-sms" key="txt-report-send-sms" name="phone" parent={DRAWER_ID}
                        label="Phone Number" labelUp block autoFocus defaultPhone={defaultPhone} onChange={e => phoneSet(e)} />
                }

                {
                    sendTo === "wa" && (
                        waActive === false ?
                            <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-5">
                                <Icon icon="triangle-exclamation" type="solid" className="text-warning me-3 mt-1" size={18} />
                                <GrowthSignal kind="paywall" feature="whatsapp" detail="send a report by WhatsApp" />
                                <div className="flex-grow-1">
                                    <div className="fw-bold text-gray-800 mb-1">WhatsApp is not active</div>
                                    <div className="fs-7 text-gray-600">
                                        The WhatsApp subscription is not active for this account.
                                    </div>
                                    {
                                        canManageAddons &&
                                        <Button className="mt-3" type="light" color="warning" icon="up-right-from-square"
                                            text="Manage Add-ons" onClick={openAddons} />
                                    }
                                </div>
                            </div>
                            :
                            <PhoneInput id="txt-report-send-wa" key="txt-report-send-wa" name="phone" parent={DRAWER_ID}
                                label="Phone Number" labelUp block autoFocus countryCodes="all" defaultPhone={defaultWhatsapp} onChange={e => phoneSet(e)} />
                    )
                }

                <TextArea name="message" block label="Message" labelUp rows={4} marginBottom={0}
                    placeholder="Add a Message (optional)..." onChange={e => messageSet(e.target.value)} />

                {
                    !waBlocked &&
                    <div className="mt-8 d-flex">
                        <Button color="success" icon="paper-plane" className="flex-fill me-3"
                            isRunning={sending} runningText="Sending..."
                            text={sendTo === "email" ? "Send Files" : "Send Link"} onClick={send} />
                        <Button type="light" color="google" icon="arrow-down-to-line"
                            isRunning={downloading} runningText="Preparing..."
                            text="Download" onClick={download} />
                    </div>
                }

            </div>
        </Drawer>
    );
}
